/**
 * FHIR MedicationRequest-aligned types for medication ordering
 * Based on HL7 FHIR R4 MedicationRequest specification
 * https://www.hl7.org/fhir/R4/medicationrequest.html
 */
import type {
  CitationSourceType,
  FHIRMedication,
  FHIRMedicationRecommendation,
} from '@eluve/llm-outputs';

// Re-export shared types from schema
export type {
  CitationSourceType,
  RecommendationCitation,
  FHIRMedication,
  FHIRDosage,
  ConflictSeverity,
  FHIRMedicationRecommendation,
} from '@eluve/llm-outputs';

// Essential fields only - simplified for 90% of use cases
export interface FHIRMedicationOrder {
  id: string;
  medication: FHIRMedication;
  dose: string; // e.g., "10 mg", "500 mg"
  frequency: string; // e.g., "once daily", "every 8 hours"
  route: string; // e.g., "oral", "topical"
  duration?: string; // e.g., "7 days", "30 days"
  quantity: number; // Total to dispense
  refills: number; // Number of refills allowed
  instructions?: string; // Additional instructions
}

// Order form values for the form component
export interface FHIROrderFormValues {
  dose: string;
  frequency: string;
  route: string;
  duration: string;
  quantity: number;
  refills: number;
  instructions: string;
}

// Props for the main component
export interface FHIRMedicineRecommendationsProps {
  tenantId: string;
  appointmentId: string;
  isReadonly?: boolean;
  recommendations?: FHIRMedicationRecommendation[];
  orders?: FHIRMedicationOrder[];
  onOrderSubmit?: (order: FHIRMedicationOrder) => void;
  onOrderRemove?: (orderId: string) => void;
  onOrderUpdate?: (order: FHIRMedicationOrder) => void;
}

// Common dropdown options
export const FREQUENCY_OPTIONS = [
  { value: 'once daily', label: 'Once daily' },
  { value: 'twice daily', label: 'Twice daily' },
  { value: 'three times daily', label: 'Three times daily' },
  { value: 'four times daily', label: 'Four times daily' },
  { value: 'every 4 hours', label: 'Every 4 hours' },
  { value: 'every 6 hours', label: 'Every 6 hours' },
  { value: 'every 8 hours', label: 'Every 8 hours' },
  { value: 'every 12 hours', label: 'Every 12 hours' },
  { value: 'as needed', label: 'As needed (PRN)' },
  { value: 'at bedtime', label: 'At bedtime' },
] as const;

export const ROUTE_OPTIONS = [
  { value: 'oral', label: 'Oral' },
  { value: 'topical', label: 'Topical' },
  { value: 'subcutaneous', label: 'Subcutaneous' },
  { value: 'intramuscular', label: 'Intramuscular' },
  { value: 'intravenous', label: 'Intravenous' },
  { value: 'inhalation', label: 'Inhalation' },
  { value: 'ophthalmic', label: 'Ophthalmic (Eye)' },
  { value: 'otic', label: 'Otic (Ear)' },
  { value: 'nasal', label: 'Nasal' },
  { value: 'rectal', label: 'Rectal' },
  { value: 'transdermal', label: 'Transdermal (Patch)' },
  { value: 'sublingual', label: 'Sublingual' },
] as const;

export const FORM_OPTIONS = [
  { value: 'tablet', label: 'Tablet' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'solution', label: 'Solution' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'syrup', label: 'Syrup' },
  { value: 'injection', label: 'Injection' },
  { value: 'cream', label: 'Cream' },
  { value: 'ointment', label: 'Ointment' },
  { value: 'gel', label: 'Gel' },
  { value: 'patch', label: 'Patch' },
  { value: 'inhaler', label: 'Inhaler' },
  { value: 'drops', label: 'Drops' },
  { value: 'powder', label: 'Powder' },
  { value: 'suppository', label: 'Suppository' },
  { value: 'lozenge', label: 'Lozenge' },
] as const;

// Icon mapping for citation source types
export const CITATION_SOURCE_ICONS: Record<CitationSourceType, string> = {
  transcript: 'FileText',
  medication: 'Pill',
  lab_result: 'FlaskConical',
  condition: 'Stethoscope',
  allergy: 'AlertTriangle',
  clinical_note: 'ClipboardPen',
};

export const CITATION_SOURCE_LABELS: Record<CitationSourceType, string> = {
  transcript: 'Transcript',
  medication: 'Previous Rx',
  lab_result: 'Lab Result',
  condition: 'Condition',
  allergy: 'Allergy',
  clinical_note: 'Clinical Note',
};
